import { format } from 'date-fns'
import { saveAs } from 'file-saver'
import JSZip from 'jszip'
import type { IconType } from 'react-icons'
import { FaJava } from 'react-icons/fa6'
import {
  PiFile,
  PiFileCode,
  PiFileCss,
  PiFileHtml,
  PiFileImage,
  PiFileJs,
  PiFileJsx,
  PiFilePdf,
  PiFileRs,
  PiFileSql,
  PiFileSvg,
  PiFileText,
  PiFileTs,
  PiFileTsx,
  PiFileVue,
  PiFileZip,
  PiMicrosoftExcelLogo,
  PiMicrosoftPowerpointLogo,
  PiMicrosoftWordLogo,
} from 'react-icons/pi'
import {
  SiC,
  SiCplusplus,
  SiDart,
  SiGnubash,
  SiGo,
  SiJson,
  SiKotlin,
  SiLua,
  SiMarkdown,
  SiPhp,
  SiPython,
  SiR,
  SiRuby,
  SiSharp,
  SiSwift,
  SiToml,
  SiYaml,
} from 'react-icons/si'
import { toast } from 'sonner'
import { twMerge } from 'tailwind-merge'

import type { Attachment } from '@/components/common/Attachment'

import {
  SPREADSHEET_FILE_EXTENSIONS,
  SPREADSHEET_FILE_MIME_TYPES,
  SPREADSHEET_MIME_TO_EXTENSIONS,
} from '@/constants/file'

import type {
  File as ServerFile,
  PotentiallyUnhydratedFile,
  UnhydratedFile,
} from '@/types/File'
import type { UUID } from '@/types/Id'

// using 1000 instead of 1024 to be consistent with the file size in the API;
// plus that's what macOS does in the Finder
export const KILOBYTE = 1000
export const MEGABYTE = KILOBYTE * KILOBYTE
export const GIGABYTE = KILOBYTE * KILOBYTE * KILOBYTE

// A decoded stored name can contain path separators (e.g. an encoded '%2F');
// ZIP entries treat '/' as folder boundaries, so keep only the basename to
// keep every file at the archive root (mirrors the server's attachment
// Content-Disposition basename rule).
const zipEntryBaseName = (storedName: string) => {
  const cleaned = cleanFileName(storedName).replace(/\\/g, '/')
  const base = cleaned.slice(cleaned.lastIndexOf('/') + 1)
  return base === '' || base === '.' || base === '..' ? 'download' : base
}

const uniquifyZipEntryName = (name: string, usedNames: Set<string>) => {
  let candidate = name
  for (let i = 1; usedNames.has(candidate); i++) {
    const dotIndex = name.lastIndexOf('.')
    candidate =
      dotIndex > 0
        ? `${name.slice(0, dotIndex)} (${i})${name.slice(dotIndex)}`
        : `${name} (${i})`
  }
  usedNames.add(candidate)
  return candidate
}

// List-sourced records carry url === '' at runtime (the field is omitted from
// the UnhydratedFile type); presence of a non-empty url marks a hydrated file.
export const isUnhydratedFile = (
  file: PotentiallyUnhydratedFile,
): file is UnhydratedFile => {
  const { url } = file as ServerFile
  return url == null || url.length === 0
}

export const isHydratedFile = (
  file: PotentiallyUnhydratedFile,
): file is ServerFile => !isUnhydratedFile(file)

// Store-facing shape: stores typed on ServerFile hold unhydrated list records
// with url === '' until a consumer hydrates them via useFile/fetchFile.
export const asServerFile = (file: PotentiallyUnhydratedFile): ServerFile =>
  isUnhydratedFile(file) ? { ...file, url: '' } : file

export const downloadAsZip = async (
  files: Array<Pick<ServerFile, 'name' | 'url'>>,
  title?: string,
) => {
  const zip = new JSZip()
  toast.info('Initiating ZIP download...')

  // fetch all files in parallel
  const results = await Promise.all(
    files.map(async (file) => {
      try {
        const response = await fetch(file.url)
        return {
          name: file.name,
          blob: response.ok ? await response.blob() : null,
        }
      } catch {
        return { name: file.name, blob: null }
      }
    }),
  )

  const failedNames = results
    .filter((result) => result.blob === null)
    .map((result) => result.name)
  if (failedNames.length > 0) {
    toast.error(
      `Failed to download ${failedNames.length} file${failedNames.length === 1 ? '' : 's'}: ${failedNames.join(', ')}`,
    )
  }

  const usedNames = new Set<string>()
  for (const { name, blob } of results) {
    if (!blob) continue
    zip.file(uniquifyZipEntryName(zipEntryBaseName(name), usedNames), blob, {
      binary: true,
    })
  }
  // nothing succeeded; don't save an empty ZIP
  if (usedNames.size === 0) return

  // generate ZIP and trigger download
  const content = await zip.generateAsync({ type: 'blob' })
  saveAs(content, `${title ?? 'attachments'}.zip`)
}

export const downloadFile = async (
  url: string,
  filename?: string,
  downloadUrl?: string | null,
) => {
  // The filename is used verbatim: callers pass a display-ready name —
  // cleanFileName(file.name) for percent-encoded stored S3 names, raw
  // human-authored titles for exports — so the saved name always matches
  // what the UI shows and nothing is ever decoded twice.
  // Only hand http(s) URLs to the anchor path; anything else (malformed,
  // javascript:, data:) falls back to the blob fetch of the preview URL.
  // The constant base resolves relative URLs, which inherit the page's
  // http(s) scheme; the URL parser also strips tab/newline like an href.
  let safeDownloadUrl: string | null = null
  if (downloadUrl) {
    try {
      const parsed = new URL(downloadUrl, 'http://relative.invalid')
      if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
        safeDownloadUrl = downloadUrl
      }
    } catch {
      // Malformed URL: treated as absent.
    }
  }
  if (safeDownloadUrl) {
    // Presigned attachment URL: hand it straight to the browser download
    // manager so the file streams to disk instead of buffering in JS.
    // target=_blank keeps an expired/denied presign from unloading the app:
    // the URL is cross-origin, so on failure the browser would otherwise
    // navigate the current tab to the raw storage error page.
    const anchor = document.createElement('a')
    anchor.href = safeDownloadUrl
    anchor.download = filename ?? ''
    anchor.target = '_blank'
    anchor.rel = 'noopener noreferrer'
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    return
  }
  // Fallback for stale cached metadata without a download_url: fetch as blob
  // to handle cross-origin URLs (e.g., S3 presigned URLs)
  try {
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`Download failed with status ${response.status}`)
    }
    const blob = await response.blob()
    saveAs(blob, filename || 'download')
  } catch {
    // Last resort (parity with the pre-download_url behavior): the browser
    // may still be able to open the URL directly even when fetch is blocked
    // (e.g. cross-origin without CORS). Only report failure if the popup
    // was blocked too.
    const opened = window.open(url, '_blank', 'noopener,noreferrer')
    if (!opened) {
      toast.error('Download failed')
    }
    return
  }
}

const DOCX_EXTENSION = /\.docx$/i

/**
 * A document's name as a .docx filename.
 *
 * Documents are usually named without an extension ("Technical Volume"), and a
 * download saved under that name lands in Downloads with nothing to say what it
 * is — the OS falls back to opening it as text. One that came in from an upload
 * keeps its own extension, so appending blindly would produce
 * "proposal.docx.docx".
 */
export const docxFileName = (documentName: string): string => {
  const base = documentName.trim().replace(DOCX_EXTENSION, '') || 'document'
  return `${base}.docx`
}

/** Shown wherever a file turns out not to be openable in the Word editor. */
export const NOT_A_WORD_DOCUMENT_MESSAGE =
  "This file isn't a Word document. Only .docx files can be opened in the editor."

/** What the offer to edit a stored .docx says, on every surface that makes it. */
export const OPEN_IN_EDITOR_LABEL = 'Open in editor'
/** And what it says once the file already opens as a document of its own. */
export const OPEN_DOCUMENT_LABEL = 'Open document'

/**
 * Whether these bytes are a Word document the editor can open.
 *
 * The name proves nothing: files come off solicitation portals mislabeled all
 * the time, and a PowerPoint saved as `.docx` is still a PowerPoint. A .docx is
 * a zip whose main part is `word/document.xml`, which is exactly what the
 * editor's converter reaches for — miss it and the converter throws from deep
 * inside itself, taking the page down with it.
 */
export const isWordDocument = async (blob: Blob): Promise<boolean> => {
  try {
    const zip = await JSZip.loadAsync(await blob.arrayBuffer())
    return zip.file('word/document.xml') !== null
  } catch {
    // Not a zip at all: a PDF, an HTML error page, a truncated upload.
    return false
  }
}

/**
 * Which version of a stored file a download is, as S3 answered it.
 *
 * The ETag: a different string after every write, the same until the next one.
 * Read off the response the bytes arrived in, so it names those bytes and not
 * whatever the file became a moment later — a save that quotes it is refused if
 * somebody else's has landed since.
 *
 * Null where the header is not ours to read, and a save with no version to
 * quote is written the way saves were written before this was asked for.
 */
export const storedVersionOf = (res: Response): string | null =>
  res.headers.get('etag')?.replaceAll('"', '') || null

/** Names what an upload landed without reading out the whole list. */
export const describeFiles = (files: { name: string }[]): string =>
  files.length === 1
    ? `"${cleanFileName(files[0].name)}"`
    : `${files.length} files`

export const cleanFileName = (name: string): string => {
  try {
    const decoded = decodeURIComponent(name).replaceAll('+', ' ')
    return decoded
  } catch (error) {
    return name
  }
}

export const cleanFileNameAndRemoveExtension = (name: string) => {
  return cleanFileName(name).replace(/\.[^.]+$/, '')
}

/**
 * Strip query/hash params from a file URL so presigned URL rotations do not
 * look like a different browser preview.
 */
export const getStableFileUrlKey = (url: string | null | undefined): string => {
  if (!url) return ''

  try {
    const parsed = new URL(url, 'https://sweetspot.local')
    const prefix =
      parsed.origin === 'https://sweetspot.local' ? '' : parsed.origin
    return `${prefix}${parsed.pathname}`
  } catch {
    return url.split(/[?#]/)[0] ?? url
  }
}

export const isFileImportedFromSharepoint = (
  file: PotentiallyUnhydratedFile,
): boolean => {
  return (
    file.upload_source === 'sharepoint' ||
    file.organization_file?.sharepoint_list_item_id != null ||
    file.organization_file?.sharepoint_list_id != null ||
    file.organization_file?.sharepoint_site_id != null
  )
}

export const isFileImportedFromGovWin = (
  file: PotentiallyUnhydratedFile,
): boolean => {
  return file.upload_source === 'govwin' || file.govwin_file != null
}

interface FileTypeIconVisual {
  Icon: IconType
  colorClassName: string
}

// NOTE: anything above text-*-600 does NOT work for react-icons/pi, so DO NOT CHANGE
const NEUTRAL_ICON_COLOR = 'text-primary-600 dark:text-primary-300'

const fileTypeIconVisuals = (
  Icon: IconType,
  colorClassName: string,
  extensions: string[],
): Array<[string, FileTypeIconVisual]> =>
  extensions.map((extension) => [extension, { Icon, colorClassName }])

const FILE_TYPE_ICON_VISUALS: Record<string, FileTypeIconVisual> =
  Object.fromEntries([
    ...fileTypeIconVisuals(
      PiMicrosoftExcelLogo,
      'text-green-600 dark:text-green-400',
      ['csv', 'tsv', 'xls', 'xlsx', 'xlsm', 'xltx', 'xltm'],
    ),
    ...fileTypeIconVisuals(
      PiMicrosoftWordLogo,
      'text-blue-600 dark:text-blue-400',
      ['doc', 'docx'],
    ),
    ...fileTypeIconVisuals(
      PiMicrosoftPowerpointLogo,
      'text-red-600 dark:text-red-500',
      ['ppt', 'pptx'],
    ),
    ...fileTypeIconVisuals(PiFilePdf, 'text-red-600 dark:text-red-400', [
      'pdf',
    ]),
    ...fileTypeIconVisuals(PiFileImage, NEUTRAL_ICON_COLOR, [
      'gif',
      'jpg',
      'jpeg',
      'png',
      'webp',
      'bmp',
      'tiff',
      'ico',
    ]),
    ...fileTypeIconVisuals(PiFileSvg, NEUTRAL_ICON_COLOR, ['svg']),
    ...fileTypeIconVisuals(PiFileZip, NEUTRAL_ICON_COLOR, [
      'zip',
      'tar',
      'gz',
      '7z',
      'rar',
    ]),
    ...fileTypeIconVisuals(PiFileText, NEUTRAL_ICON_COLOR, [
      'odt',
      'rtf',
      'tex',
      'text',
      'txt',
      'log',
    ]),
    ...fileTypeIconVisuals(SiMarkdown, NEUTRAL_ICON_COLOR, ['md', 'markdown']),
    // Source code and structured data/config formats: dedicated file glyphs
    // where Phosphor has them, brand glyphs where it does not, each tinted
    // with the language's conventional color. Config formats stay neutral.
    ...fileTypeIconVisuals(SiPython, 'text-blue-600 dark:text-blue-400', [
      'py',
    ]),
    ...fileTypeIconVisuals(PiFileJs, 'text-amber-500 dark:text-amber-400', [
      'js',
      'mjs',
      'cjs',
    ]),
    ...fileTypeIconVisuals(PiFileJsx, 'text-amber-500 dark:text-amber-400', [
      'jsx',
    ]),
    ...fileTypeIconVisuals(PiFileTs, 'text-blue-600 dark:text-blue-400', [
      'ts',
    ]),
    ...fileTypeIconVisuals(PiFileTsx, 'text-blue-600 dark:text-blue-400', [
      'tsx',
    ]),
    ...fileTypeIconVisuals(SiC, 'text-blue-600 dark:text-blue-400', ['c', 'h']),
    ...fileTypeIconVisuals(SiCplusplus, 'text-sky-600 dark:text-sky-400', [
      'cpp',
      'hpp',
      'cc',
    ]),
    ...fileTypeIconVisuals(SiSharp, 'text-violet-600 dark:text-violet-400', [
      'cs',
    ]),
    ...fileTypeIconVisuals(PiFileRs, 'text-orange-600 dark:text-orange-400', [
      'rs',
    ]),
    ...fileTypeIconVisuals(PiFileSql, 'text-teal-600 dark:text-teal-400', [
      'sql',
    ]),
    ...fileTypeIconVisuals(PiFileCss, 'text-sky-600 dark:text-sky-400', [
      'css',
      'scss',
      'less',
    ]),
    ...fileTypeIconVisuals(PiFileHtml, 'text-orange-600 dark:text-orange-400', [
      'html',
      'htm',
    ]),
    ...fileTypeIconVisuals(PiFileVue, 'text-green-600 dark:text-green-400', [
      'vue',
    ]),
    ...fileTypeIconVisuals(FaJava, 'text-red-600 dark:text-red-400', ['java']),
    ...fileTypeIconVisuals(SiGo, 'text-cyan-600 dark:text-cyan-400', ['go']),
    ...fileTypeIconVisuals(SiRuby, 'text-red-600 dark:text-red-400', ['rb']),
    ...fileTypeIconVisuals(SiPhp, 'text-indigo-600 dark:text-indigo-400', [
      'php',
    ]),
    ...fileTypeIconVisuals(SiSwift, 'text-orange-600 dark:text-orange-400', [
      'swift',
    ]),
    ...fileTypeIconVisuals(SiKotlin, 'text-purple-600 dark:text-purple-400', [
      'kt',
    ]),
    ...fileTypeIconVisuals(
      SiGnubash,
      'text-emerald-600 dark:text-emerald-400',
      ['sh', 'bash', 'zsh'],
    ),
    ...fileTypeIconVisuals(SiR, 'text-blue-600 dark:text-blue-400', ['r']),
    ...fileTypeIconVisuals(SiLua, 'text-blue-600 dark:text-blue-400', ['lua']),
    ...fileTypeIconVisuals(SiDart, 'text-sky-600 dark:text-sky-400', ['dart']),
    ...fileTypeIconVisuals(SiJson, 'text-amber-500 dark:text-amber-400', [
      'json',
      'jsonl',
      'ndjson',
    ]),
    ...fileTypeIconVisuals(SiYaml, NEUTRAL_ICON_COLOR, ['yaml', 'yml']),
    ...fileTypeIconVisuals(SiToml, NEUTRAL_ICON_COLOR, ['toml']),
    ...fileTypeIconVisuals(PiFileCode, NEUTRAL_ICON_COLOR, [
      'ini',
      'cfg',
      'conf',
      'xml',
      'ps1',
    ]),
  ])

export const getFileTypeIcon = (extension: string, className?: string) => {
  const visual = FILE_TYPE_ICON_VISUALS[extension.toLowerCase()] ?? {
    Icon: PiFile,
    colorClassName: NEUTRAL_ICON_COLOR,
  }
  return (
    <visual.Icon
      className={twMerge('w-[18px] h-[18px]', visual.colorClassName, className)}
    />
  )
}

export const getFileTypeIconFromName = (name: string) => {
  const extension = name.split('.').pop()?.toLowerCase()
  return getFileTypeIcon(extension || '')
}

export const getFileExtensionFromName = (name: string) => {
  const trimmedName = name.trim()
  const urlSuffixIndex = trimmedName.search(/[?#]/)
  const nameBeforeUrlSuffix =
    urlSuffixIndex >= 0 ? trimmedName.slice(0, urlSuffixIndex) : trimmedName
  const dotBeforeUrlSuffix = nameBeforeUrlSuffix.lastIndexOf('.')
  const lastDotIndex =
    dotBeforeUrlSuffix >= 0 ? dotBeforeUrlSuffix : trimmedName.lastIndexOf('.')
  if (lastDotIndex < 0) {
    return ''
  }

  const extensionEndIndex =
    urlSuffixIndex > lastDotIndex ? urlSuffixIndex : trimmedName.length
  return trimmedName
    .slice(lastDotIndex + 1, extensionEndIndex)
    .toLowerCase()
    .trim()
}

const normalizeMediaType = (mediaType: string | null | undefined) => {
  return mediaType?.split(';', 1)[0]?.toLowerCase().trim() ?? ''
}

export const WORD_MEDIA_TYPE =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'

/**
 * Whether a stored file is a Word document, going by what it says about
 * itself. The bytes are the proof (see isWordDocument); this is the answer for
 * a file not in hand, which is where the question is usually asked.
 */
export const isWordFile = (file: {
  name: string
  content_type?: string | null
}): boolean =>
  normalizeMediaType(file.content_type) === WORD_MEDIA_TYPE ||
  DOCX_EXTENSION.test(file.name)

const MEDIA_TYPE_TO_EXTENSION: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'application/pdf': 'pdf',
  'text/plain': 'txt',
  'text/html': 'html',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'docx',
  'application/vnd.ms-excel': 'xls',
  ...Object.fromEntries(
    Object.entries(SPREADSHEET_MIME_TO_EXTENSIONS).map(
      ([mediaType, [extension]]) => [mediaType, extension.slice(1)],
    ),
  ),
  'application/vnd.ms-powerpoint': 'ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    'pptx',
  'application/zip': 'zip',
}

export const getFileExtensionFromMediaType = (
  mediaType: string,
): string | undefined => {
  return MEDIA_TYPE_TO_EXTENSION[normalizeMediaType(mediaType)]
}

export type BrowserPreviewMode =
  | 'spreadsheet'
  | 'presentation'
  | 'html'
  | 'markdown'
  | 'text'
  | 'pdf'
  | 'image'
  | 'download'

/**
 * PDF-coordinate tools are an explicit product mode, not a file-type fallback.
 * Generic file previews should omit this option so supported Office files use
 * their native browser renderer first.
 */
export type BrowserPreviewIntent = 'default' | 'pdf-coordinates'

export interface BrowserPreviewOptions {
  intent?: BrowserPreviewIntent
}

const SPREADSHEET_VIEWER_EXTENSIONS = new Set(SPREADSHEET_FILE_EXTENSIONS)

const SPREADSHEET_VIEWER_MIME_TYPES = new Set(SPREADSHEET_FILE_MIME_TYPES)

const PRESENTATION_VIEWER_EXTENSIONS = new Set(['pptx'])

const NON_NATIVE_PRESENTATION_EXTENSIONS = new Set([
  'odp',
  'otp',
  'ppt',
  'pptm',
  'ppam',
  'pps',
  'ppsx',
  'ppsm',
  'pot',
  'potx',
  'potm',
  'sldm',
  'sldx',
  'thmx',
])

const PRESENTATION_VIEWER_MIME_TYPES = new Set([
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
])

const PDF_VIEWER_MIME_TYPES = new Set([
  'application/pdf',
  'application/x-pdf',
  'application/acrobat',
])

const IMAGE_VIEWER_EXTENSIONS = new Set([
  'jpg',
  'jpeg',
  'png',
  'webp',
  'gif',
  'svg',
  'avif',
  'bmp',
  'ico',
])

const IMAGE_VIEWER_MIME_TYPES = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/svg+xml',
  'image/avif',
  'image/bmp',
  'image/x-icon',
  'image/vnd.microsoft.icon',
])

const HTML_VIEWER_EXTENSIONS = new Set(['html', 'htm'])

const MARKDOWN_VIEWER_EXTENSIONS = new Set(['md', 'markdown'])

// Plain-text and source-code formats the native text viewer renders as
// monospace text (csv/tsv belong to the spreadsheet viewer; html/markdown
// have richer renderers above).
const TEXT_VIEWER_EXTENSIONS = new Set([
  'txt',
  'text',
  'log',
  'json',
  'jsonl',
  'ndjson',
  'yaml',
  'yml',
  'toml',
  'ini',
  'cfg',
  'conf',
  'xml',
  'py',
  'js',
  'jsx',
  'ts',
  'tsx',
  'mjs',
  'cjs',
  'java',
  'c',
  'h',
  'cpp',
  'hpp',
  'cc',
  'cs',
  'go',
  'rs',
  'rb',
  'php',
  'swift',
  'kt',
  'sql',
  'sh',
  'bash',
  'zsh',
  'ps1',
  'r',
  'lua',
  'dart',
  'css',
  'scss',
  'less',
])

const TEXT_VIEWER_MIME_TYPES = new Set([
  'application/json',
  'application/xml',
  'application/x-yaml',
  'application/yaml',
  'application/toml',
  'application/javascript',
  'application/x-python-code',
  'application/x-sh',
])

const RECOGNIZED_BROWSER_FILE_EXTENSIONS = new Set([
  ...Object.values(MEDIA_TYPE_TO_EXTENSION),
  ...SPREADSHEET_VIEWER_EXTENSIONS,
  ...PRESENTATION_VIEWER_EXTENSIONS,
  ...NON_NATIVE_PRESENTATION_EXTENSIONS,
  ...HTML_VIEWER_EXTENSIONS,
  ...MARKDOWN_VIEWER_EXTENSIONS,
  ...IMAGE_VIEWER_EXTENSIONS,
  ...TEXT_VIEWER_EXTENSIONS,
])

type BrowserPreviewFile = Pick<
  ServerFile,
  'name' | 'content_type' | 'pdf_url' | 'size'
>
type BrowserPreviewMessageFile = Pick<ServerFile, 'name' | 'content_type'>

type NativeBrowserPreviewMode = Extract<
  BrowserPreviewMode,
  'spreadsheet' | 'presentation' | 'html' | 'markdown' | 'text' | 'image'
>

const getNativeBrowserPreviewMode = (
  file: Pick<BrowserPreviewFile, 'name' | 'content_type'>,
): NativeBrowserPreviewMode | null => {
  const extension = getFileExtensionFromName(file.name)

  // A recognized extension is authoritative. Providers occasionally attach a
  // stale or generic content type, and checking MIME types first can otherwise
  // send a PPTX to the spreadsheet renderer (or vice versa).
  if (SPREADSHEET_VIEWER_EXTENSIONS.has(extension)) {
    return 'spreadsheet'
  }
  if (PRESENTATION_VIEWER_EXTENSIONS.has(extension)) {
    return 'presentation'
  }
  if (HTML_VIEWER_EXTENSIONS.has(extension)) {
    return 'html'
  }
  if (MARKDOWN_VIEWER_EXTENSIONS.has(extension)) {
    return 'markdown'
  }
  if (IMAGE_VIEWER_EXTENSIONS.has(extension)) {
    return 'image'
  }
  if (TEXT_VIEWER_EXTENSIONS.has(extension)) {
    return 'text'
  }

  // Known non-native formats must never be promoted to a different renderer by
  // conflicting MIME metadata. Unknown/generic names still fall back to MIME.
  if (RECOGNIZED_BROWSER_FILE_EXTENSIONS.has(extension)) {
    return null
  }

  const contentType = normalizeMediaType(file.content_type)
  if (SPREADSHEET_VIEWER_MIME_TYPES.has(contentType)) {
    return 'spreadsheet'
  }
  if (PRESENTATION_VIEWER_MIME_TYPES.has(contentType)) {
    return 'presentation'
  }
  if (contentType === 'text/html') {
    return 'html'
  }
  if (contentType === 'text/markdown' || contentType === 'text/x-markdown') {
    return 'markdown'
  }
  if (IMAGE_VIEWER_MIME_TYPES.has(contentType)) {
    return 'image'
  }
  // Generic text fallback last: the richer text renderers above (html,
  // markdown, csv-as-spreadsheet) claim their MIME types first.
  if (
    TEXT_VIEWER_MIME_TYPES.has(contentType) ||
    contentType.startsWith('text/')
  ) {
    return 'text'
  }

  return null
}

const isPdfViewerSupportedFile = (
  file: Pick<BrowserPreviewFile, 'name' | 'content_type'>,
) => {
  const extension = getFileExtensionFromName(file.name)
  if (extension === 'pdf') {
    return true
  }
  if (RECOGNIZED_BROWSER_FILE_EXTENSIONS.has(extension)) {
    return false
  }
  return PDF_VIEWER_MIME_TYPES.has(normalizeMediaType(file.content_type))
}

export const isSpreadsheetViewerSupportedFile = (
  file: Pick<BrowserPreviewFile, 'name' | 'content_type'>,
) => {
  return getNativeBrowserPreviewMode(file) === 'spreadsheet'
}

export const isHtmlViewerSupportedFile = (
  file: Pick<BrowserPreviewFile, 'name' | 'content_type'>,
) => {
  return getNativeBrowserPreviewMode(file) === 'html'
}

export const isMarkdownViewerSupportedFile = (
  file: Pick<BrowserPreviewFile, 'name' | 'content_type'>,
) => {
  return getNativeBrowserPreviewMode(file) === 'markdown'
}

export const isTextViewerSupportedFile = (
  file: Pick<BrowserPreviewFile, 'name' | 'content_type'>,
) => {
  return getNativeBrowserPreviewMode(file) === 'text'
}

export const isImageViewerSupportedFile = (
  file: Pick<BrowserPreviewFile, 'name' | 'content_type'>,
) => {
  return getNativeBrowserPreviewMode(file) === 'image'
}

export const isPresentationViewerSupportedFile = (
  file: Pick<BrowserPreviewFile, 'name' | 'content_type'>,
) => {
  return getNativeBrowserPreviewMode(file) === 'presentation'
}

export const isNativeOfficePreviewMode = (
  previewMode: BrowserPreviewMode,
): previewMode is 'spreadsheet' | 'presentation' => {
  return previewMode === 'spreadsheet' || previewMode === 'presentation'
}

export const isLegacySpreadsheetFile = (file: BrowserPreviewMessageFile) => {
  const extension = getFileExtensionFromName(file.name)
  if (extension === 'xls') {
    return true
  }
  if (SPREADSHEET_VIEWER_EXTENSIONS.has(extension)) {
    return false
  }

  const contentType = normalizeMediaType(file.content_type)
  return contentType === 'application/vnd.ms-excel'
}

export const isLegacyPresentationFile = (file: BrowserPreviewMessageFile) => {
  const extension = getFileExtensionFromName(file.name)
  if (extension === 'ppt') {
    return true
  }
  if (PRESENTATION_VIEWER_EXTENSIONS.has(extension)) {
    return false
  }

  const contentType = normalizeMediaType(file.content_type)
  return contentType === 'application/vnd.ms-powerpoint'
}

export const getBrowserPreviewUnavailableReason = (
  file: BrowserPreviewMessageFile,
) => {
  if (isLegacySpreadsheetFile(file)) {
    return 'Legacy XLS spreadsheets are not viewable in the browser.'
  }

  if (isLegacyPresentationFile(file)) {
    return 'Legacy PPT presentations are not viewable in the browser.'
  }

  return 'This file is not viewable in the browser.'
}

export const getBrowserPreviewUnavailableMessage = (
  file: BrowserPreviewMessageFile,
) => {
  if (isLegacySpreadsheetFile(file)) {
    return 'Legacy XLS spreadsheets are not viewable in the browser. Please download the file or convert it to XLSX to preview it.'
  }

  if (isLegacyPresentationFile(file)) {
    return 'Legacy PPT presentations are not viewable in the browser. Please download the file or convert it to PPTX to preview it.'
  }

  return 'This file is not viewable in the browser. Please download it to view.'
}

/** HTML remains relatively cheap inside the sandboxed shell. */
export const MAX_HTML_PREVIEW_BYTES = 5 * 1024 * 1024
/** Markdown parsing cost grows sharply with node/line count. */
export const MAX_MARKDOWN_PREVIEW_BYTES = 1 * 1024 * 1024
/** Plain text renders as one node; the buffer itself is the only cost. */
export const MAX_TEXT_PREVIEW_BYTES = 5 * 1024 * 1024

const TEXT_PREVIEW_BYTE_LIMITS = {
  html: MAX_HTML_PREVIEW_BYTES,
  markdown: MAX_MARKDOWN_PREVIEW_BYTES,
  text: MAX_TEXT_PREVIEW_BYTES,
} as const

const getTextBufferingPreviewMode = (
  file: Pick<BrowserPreviewFile, 'name' | 'content_type'>,
): keyof typeof TEXT_PREVIEW_BYTE_LIMITS | null => {
  const mode = getNativeBrowserPreviewMode(file)
  return mode === 'html' || mode === 'markdown' || mode === 'text' ? mode : null
}

export const getTextPreviewByteLimit = (
  file: Pick<BrowserPreviewFile, 'name' | 'content_type'>,
): number =>
  TEXT_PREVIEW_BYTE_LIMITS[getTextBufferingPreviewMode(file) ?? 'html']

export const getBrowserPreviewMode = (
  file: BrowserPreviewFile,
  options?: BrowserPreviewOptions,
): BrowserPreviewMode => {
  if (options?.intent === 'pdf-coordinates') {
    return file.pdf_url || isPdfViewerSupportedFile(file) ? 'pdf' : 'download'
  }

  const nativePreviewMode = getNativeBrowserPreviewMode(file)

  // HTML/Markdown/text previews buffer (and for the first two, parse) the
  // whole source in the app origin; oversized files use their PDF rendition
  // instead.
  const textBufferingMode = getTextBufferingPreviewMode(file)
  if (
    textBufferingMode !== null &&
    file.size !== undefined &&
    file.size > TEXT_PREVIEW_BYTE_LIMITS[textBufferingMode]
  ) {
    return file.pdf_url ? 'pdf' : 'download'
  }

  if (nativePreviewMode) {
    return nativePreviewMode
  }

  if (isPdfViewerSupportedFile(file)) {
    return 'pdf'
  }

  if (file.pdf_url) {
    return 'pdf'
  }

  return 'download'
}

export const isBrowserPreviewableFile = (
  file: BrowserPreviewFile,
  options?: BrowserPreviewOptions,
) => {
  return getBrowserPreviewMode(file, options) !== 'download'
}

export const getBrowserPdfUrl = (
  file: BrowserPreviewFile & Pick<ServerFile, 'url'>,
) => {
  if (file.pdf_url) {
    return file.pdf_url
  }
  return isPdfViewerSupportedFile(file) ? file.url : null
}

export const getBrowserPreviewUrl = (
  file: BrowserPreviewFile & Pick<ServerFile, 'url'>,
) => {
  const previewMode = getBrowserPreviewMode(file)
  if (previewMode === 'download') {
    return null
  }
  return previewMode === 'pdf' ? getBrowserPdfUrl(file) : file.url
}

export const isServerFile = (file: unknown): file is ServerFile => {
  return (
    typeof file === 'object' &&
    file !== null &&
    'url' in file &&
    'name' in file &&
    'id' in file
  )
}

export const getFileExtensionFromUrl = (url: string) => {
  try {
    const pathname = new URL(url, window.location.origin).pathname
    const parts = pathname.split('.')
    if (parts.length > 1) {
      return parts.pop()?.toLowerCase() ?? ''
    }
  } catch (e) {
    const pathname = url.split(/[?#]/)[0]
    const parts = pathname.split('.')
    if (parts.length > 1) {
      return parts.pop()?.toLowerCase() ?? ''
    }
  }
  return ''
}

export const removeFileExtensionFromName = (name: string) => {
  return name.split('.').slice(0, -1).join('.')
}

export const cleanFileNameForRename = (name: string) => {
  return removeFileExtensionFromName(cleanFileName(name))
}

export const getFileLabelFromName = (name: string) => {
  const extension = name.split('.').pop()?.toLowerCase()
  switch (extension) {
    case 'csv':
      return 'Spreadsheet'
    case 'doc':
      return 'Word Document'
    case 'docx':
      return 'Word Document'
    case 'gif':
      return 'Image'
    case 'jpg':
      return 'Image'
    case 'jpeg':
      return 'Image'
    case 'png':
      return 'Image'
    case 'webp':
      return 'Image'
    case 'odt':
      return 'Open Document'
    case 'pdf':
      return 'PDF Document'
    case 'pptx':
      return 'Powerpoint Presentation'
    case 'rtf':
      return 'Rich Text Document'
    case 'tex':
      return 'LaTeX Document'
    case 'text':
      return 'Text Document'
    case 'txt':
      return 'Text Document'
    case 'xls':
      return 'Excel Spreadsheet'
    case 'xlsx':
      return 'Excel Spreadsheet'
    case 'zip':
      return 'Archive'
    default:
      return 'File'
  }
}

export const getFileTypeRawIconFromName = (name: string): IconType => {
  const extension = name.split('.').pop()?.toLowerCase()
  switch (extension) {
    case 'csv':
      return PiMicrosoftExcelLogo
    case 'doc':
      return PiMicrosoftWordLogo
    case 'docx':
      return PiMicrosoftWordLogo
    case 'gif':
      return PiFileImage
    case 'jpg':
      return PiFileImage
    case 'jpeg':
      return PiFileImage
    case 'png':
      return PiFileImage
    case 'webp':
      return PiFileImage
    case 'odt':
      return PiFileText
    case 'pdf':
      return PiFilePdf
    case 'pptx':
      return PiMicrosoftPowerpointLogo
    case 'rtf':
      return PiFileText
    case 'tex':
      return PiFileText
    case 'text':
      return PiFileText
    case 'txt':
      return PiFileText
    case 'xls':
      return PiMicrosoftExcelLogo
    case 'xlsx':
      return PiMicrosoftExcelLogo
    case 'zip':
      return PiFileZip
    default:
      return PiFile
  }
}

export const isValidImage = (file: Pick<ServerFile, 'name'>) => {
  return (
    file.name.endsWith('.jpeg') ||
    file.name.endsWith('.jpg') ||
    file.name.endsWith('.png') ||
    file.name.endsWith('.webp')
  )
}

export const getFileSizeAsHumanReadable = (
  size: number,
  decimals: number = 0,
) => {
  if (size < KILOBYTE) {
    return `${size} B`
  }
  if (size < MEGABYTE) {
    return `${(size / KILOBYTE).toFixed(decimals)} KB`
  }
  if (size < GIGABYTE) {
    return `${(size / MEGABYTE).toFixed(decimals)} MB`
  }
  return `${(size / GIGABYTE).toFixed(decimals)} GB`
}

export const serverFileToAttachment = (
  file: PotentiallyUnhydratedFile,
): Attachment => {
  const extension = getFileExtensionFromName(file.name)
  return {
    label: file.name,
    description: file.size ? getFileSizeAsHumanReadable(file.size) : 'Unknown',
    thumbnail: extension,
    href: `/files/${file.id}`,
    isLoading: false,
  }
}

export const clientFileToAttachment = (file: File): Attachment => {
  const extension = getFileExtensionFromName(file.name)
  return {
    label: file.name,
    description: file.size ? getFileSizeAsHumanReadable(file.size) : 'Unknown',
    thumbnail: extension,
    isLoading: false,
  }
}

export const getFileCreatedAt = (file: PotentiallyUnhydratedFile) => {
  const createdAt = new Date(file.created_at)
  return `Added ${format(createdAt, 'MMM do')} at ${format(createdAt, 'h:mm a')}`
}

export const isSolicitationFile = (file: PotentiallyUnhydratedFile) => {
  return !['pursuit_file', 'organization_file', 'word_template'].includes(
    file.type,
  )
}

const EMPTY_TAG_IDS: UUID[] = []

export const getFileTagIds = (
  file: PotentiallyUnhydratedFile | null | undefined,
): UUID[] => {
  if (!file) {
    return EMPTY_TAG_IDS
  }

  switch (file.type) {
    case 'word_template_file':
      return file.word_template_file?.tag_ids ?? EMPTY_TAG_IDS
    case 'pursuit_file':
      return file.pursuit_file?.tag_ids ?? EMPTY_TAG_IDS
    case 'organization_file':
      return file.organization_file?.tag_ids ?? EMPTY_TAG_IDS
    default:
      // Solicitation files (federal_contract_file, federal_grant_file, etc.)
      // use solicitation_file_tag_ids for pursuit-scoped tags
      return file.solicitation_file_tag_ids ?? EMPTY_TAG_IDS
  }
}

export const getFileTags = (file: PotentiallyUnhydratedFile) => {
  return getFileTagIds(file)
}

const sortTagIds = (tagIds: readonly string[]) => [...tagIds].sort()

export const haveSameTagIds = (
  nextTagIds: readonly string[],
  previousTagIds: readonly string[],
) => {
  const sortedNextTagIds = sortTagIds(nextTagIds)
  const sortedPreviousTagIds = sortTagIds(previousTagIds)

  return (
    sortedNextTagIds.length === sortedPreviousTagIds.length &&
    sortedNextTagIds.every((tagId, index) => {
      return tagId === sortedPreviousTagIds[index]
    })
  )
}

export const getFileFolderId = (file: PotentiallyUnhydratedFile) => {
  if (file.type === 'word_template_file') {
    return file.word_template_file?.folder_id ?? null
  }
  if (file.type === 'organization_file') {
    return file.organization_file?.folder_id ?? null
  }
  if (file.type === 'pursuit_file') {
    return file.pursuit_file?.folder_id ?? null
  }
  return null
}
